// src/pages/admin/AdminSettings.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  Button,
  TextField,
  Divider,
  Alert,
  useTheme,
  alpha,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  Palette as PaletteIcon,
  Save as SaveIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import * as adminApi from '../../services/admin';

const AdminSettings: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Email Template Settings
  const [emailTemplateSettings, setEmailTemplateSettings] = useState({
    primaryColor: '#1976d2',
    secondaryColor: '#dc004e',
    logoText: 'VALUE CHAIN GLOBAL',
    companyName: 'Value Chain Global',
    tagline: 'Connecting Opportunities Worldwide',
    footerText: 'This is an automated message, please do not reply to this email.',
    supportEmail: 'support@value-chain.global'
  });

  // Email Template Bodies
  const [emailBodies, setEmailBodies] = useState({
    registrationOTP: '',
    approved: '',
    resetPassword: '',
    userInvite: ''
  });

  const [activeEmailTab, setActiveEmailTab] = useState(0);

  // System Settings
  const [systemSettings, setSystemSettings] = useState({
    maintenanceMode: false,
    allowRegistrations: true
  });

  // System Information
  const [systemInfo, setSystemInfo] = useState<{
    version: string;
    database: { status: string; responseTime: string };
    uptime: string;
    users: { total: number; active: number };
  } | null>(null);

  useEffect(() => {
    fetchSettings();
    fetchSystemInfo();

    // Refresh system info every 30 seconds
    const interval = setInterval(fetchSystemInfo, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const settings = await adminApi.getSettings();

      // Parse email template settings
      setEmailTemplateSettings({
        primaryColor: settings.email_primary_color || '#1976d2',
        secondaryColor: settings.email_secondary_color || '#dc004e',
        logoText: settings.email_logo_text || 'VALUE CHAIN GLOBAL',
        companyName: settings.email_company_name || 'Value Chain Global',
        tagline: settings.email_tagline || 'Connecting Opportunities Worldwide',
        footerText: settings.email_footer_text || 'This is an automated message, please do not reply to this email.',
        supportEmail: settings.email_support_email || 'support@value-chain.global'
      });

      // Parse email body templates
      setEmailBodies({
        registrationOTP: settings.email_body_registration_otp || '',
        approved: settings.email_body_approved || '',
        resetPassword: settings.email_body_reset_password || '',
        userInvite: settings.email_body_user_invite || ''
      });

      // Parse settings from backend into component state
      setSystemSettings({
        maintenanceMode: settings.maintenance_mode || false,
        allowRegistrations: settings.allow_registrations !== false
      });
    } catch (err: any) {
      console.error('Failed to fetch settings:', err);
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemInfo = async () => {
    try {
      const info = await adminApi.getSystemInfo();
      setSystemInfo(info);
    } catch (err: any) {
      console.error('Failed to fetch system info:', err);
    }
  };

  const handleEmailTemplateChange = (field: keyof typeof emailTemplateSettings, value: string) => {
    setEmailTemplateSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleEmailBodyChange = (field: keyof typeof emailBodies, value: string) => {
    setEmailBodies(prev => ({ ...prev, [field]: value }));
  };

  const handleSystemSettingChange = (field: keyof typeof systemSettings) => {
    setSystemSettings(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);

      // Build settings object in backend format
      const settingsToSave = {
        // Email template settings
        email_primary_color: { value: emailTemplateSettings.primaryColor, type: 'string', description: 'Primary brand color for emails' },
        email_secondary_color: { value: emailTemplateSettings.secondaryColor, type: 'string', description: 'Secondary brand color for emails' },
        email_logo_text: { value: emailTemplateSettings.logoText, type: 'string', description: 'Logo text in email header' },
        email_company_name: { value: emailTemplateSettings.companyName, type: 'string', description: 'Company name for emails' },
        email_tagline: { value: emailTemplateSettings.tagline, type: 'string', description: 'Company tagline in email header' },
        email_footer_text: { value: emailTemplateSettings.footerText, type: 'string', description: 'Footer disclaimer text' },
        email_support_email: { value: emailTemplateSettings.supportEmail, type: 'string', description: 'Support email address' },

        // Email body templates
        email_body_registration_otp: { value: emailBodies.registrationOTP, type: 'string', description: 'Registration OTP email body template' },
        email_body_approved: { value: emailBodies.approved, type: 'string', description: 'Account approved email body template' },
        email_body_reset_password: { value: emailBodies.resetPassword, type: 'string', description: 'Password reset email body template' },
        email_body_user_invite: { value: emailBodies.userInvite, type: 'string', description: 'User invitation email body template' },

        maintenance_mode: { value: systemSettings.maintenanceMode, type: 'boolean', description: 'Enable maintenance mode' },
        allow_registrations: { value: systemSettings.allowRegistrations, type: 'boolean', description: 'Allow new user registrations' }
      };

      await adminApi.updateSettings(settingsToSave);

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      console.error('Failed to save settings:', err);
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SettingsIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight={600}>
            System Settings
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSaveSettings}
          disabled={saving}
          sx={{ px: 3 }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSaveSuccess(false)}>
          Settings saved successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Email Template Design */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
              Email Template Design
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Primary Color"
                  type="color"
                  value={emailTemplateSettings.primaryColor}
                  onChange={(e) => handleEmailTemplateChange('primaryColor', e.target.value)}
                  helperText="Main brand color used in email headers and buttons"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Secondary Color"
                  type="color"
                  value={emailTemplateSettings.secondaryColor}
                  onChange={(e) => handleEmailTemplateChange('secondaryColor', e.target.value)}
                  helperText="Accent color for highlights and important elements"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Logo Text"
                  value={emailTemplateSettings.logoText}
                  onChange={(e) => handleEmailTemplateChange('logoText', e.target.value)}
                  helperText="Text displayed in email header"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={emailTemplateSettings.companyName}
                  onChange={(e) => handleEmailTemplateChange('companyName', e.target.value)}
                  helperText="Full company name"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tagline"
                  value={emailTemplateSettings.tagline}
                  onChange={(e) => handleEmailTemplateChange('tagline', e.target.value)}
                  helperText="Displayed below logo in email header"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Support Email"
                  value={emailTemplateSettings.supportEmail}
                  onChange={(e) => handleEmailTemplateChange('supportEmail', e.target.value)}
                  helperText="Contact email shown in footer"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Footer Text"
                  value={emailTemplateSettings.footerText}
                  onChange={(e) => handleEmailTemplateChange('footerText', e.target.value)}
                  helperText="Disclaimer text in email footer"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Preview:</strong> These settings control the design of all automated emails sent by the system, including registration confirmations, password resets, and user approvals.
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Email Body Templates */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <EmailIcon sx={{ mr: 1, color: 'secondary.main' }} />
              Email Body Templates
            </Typography>

            <Tabs
              value={activeEmailTab}
              onChange={(e, newValue) => setActiveEmailTab(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
              <Tab label="Registration OTP" />
              <Tab label="Account Approved" />
              <Tab label="Password Reset" />
              <Tab label="User Invitation" />
            </Tabs>

            {activeEmailTab === 0 && (
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={15}
                  label="Registration OTP Email Body"
                  value={emailBodies.registrationOTP}
                  onChange={(e) => handleEmailBodyChange('registrationOTP', e.target.value)}
                  helperText="HTML template for registration OTP email. Use {{OTP}}, {{EXPIRY_MINUTES}}, {{COMPANY_NAME}} as placeholders."
                  sx={{ fontFamily: 'monospace' }}
                />
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>Available placeholders:</strong> {`{{OTP}}`}, {`{{EXPIRY_MINUTES}}`}, {`{{COMPANY_NAME}}`}
                </Alert>
              </Box>
            )}

            {activeEmailTab === 1 && (
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={15}
                  label="Account Approved Email Body"
                  value={emailBodies.approved}
                  onChange={(e) => handleEmailBodyChange('approved', e.target.value)}
                  helperText="HTML template for account approval email. Use {{COMPANY_NAME}}, {{LOGIN_URL}} as placeholders."
                  sx={{ fontFamily: 'monospace' }}
                />
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>Available placeholders:</strong> {`{{COMPANY_NAME}}`}, {`{{LOGIN_URL}}`}
                </Alert>
              </Box>
            )}

            {activeEmailTab === 2 && (
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={15}
                  label="Password Reset Email Body"
                  value={emailBodies.resetPassword}
                  onChange={(e) => handleEmailBodyChange('resetPassword', e.target.value)}
                  helperText="HTML template for password reset email. Use {{RESET_LINK}}, {{COMPANY_NAME}} as placeholders."
                  sx={{ fontFamily: 'monospace' }}
                />
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>Available placeholders:</strong> {`{{RESET_LINK}}`}, {`{{COMPANY_NAME}}`}
                </Alert>
              </Box>
            )}

            {activeEmailTab === 3 && (
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={15}
                  label="User Invitation Email Body"
                  value={emailBodies.userInvite}
                  onChange={(e) => handleEmailBodyChange('userInvite', e.target.value)}
                  helperText="HTML template for user invitation email. Use {{INVITE_LINK}}, {{USER_TYPE}}, {{COMPANY_NAME}} as placeholders."
                  sx={{ fontFamily: 'monospace' }}
                />
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>Available placeholders:</strong> {`{{INVITE_LINK}}`}, {`{{USER_TYPE}}`}, {`{{COMPANY_NAME}}`}
                </Alert>
              </Box>
            )}

            <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Note:</strong> Leave templates empty to use default templates. HTML and inline CSS are supported. Make sure to test emails after customization.
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* System Configuration */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <SecurityIcon sx={{ mr: 1, color: 'warning.main' }} />
              System Configuration
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <CloudIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Maintenance Mode"
                  secondary="Disable public access to the site"
                />
                <Switch
                  checked={systemSettings.maintenanceMode}
                  onChange={() => handleSystemSettingChange('maintenanceMode')}
                  color="warning"
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <NotificationsIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Allow Registrations"
                  secondary="Enable new user registrations"
                />
                <Switch
                  checked={systemSettings.allowRegistrations}
                  onChange={() => handleSystemSettingChange('allowRegistrations')}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* System Information */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <StorageIcon sx={{ mr: 1, color: 'success.main' }} />
              System Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CloudIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Application Version
                      </Typography>
                    </Box>
                    <Typography variant="h5" fontWeight={600} color="primary.main">
                      {systemInfo ? `v${systemInfo.version}` : <CircularProgress size={20} />}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <StorageIcon sx={{ mr: 1, color: 'success.main', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Database Status
                      </Typography>
                    </Box>
                    {systemInfo ? (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            variant="h5"
                            fontWeight={600}
                            color={systemInfo.database.status === 'Connected' ? 'success.main' : 'error.main'}
                          >
                            {systemInfo.database.status}
                          </Typography>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: systemInfo.database.status === 'Connected' ? 'success.main' : 'error.main',
                              ml: 1,
                              animation: 'pulse 2s ease-in-out infinite',
                              '@keyframes pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.4 }
                              }
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {systemInfo.database.responseTime}
                        </Typography>
                      </>
                    ) : (
                      <CircularProgress size={20} />
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <NotificationsIcon sx={{ mr: 1, color: 'info.main', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Total Users
                      </Typography>
                    </Box>
                    <Typography variant="h5" fontWeight={600} color="info.main">
                      {systemInfo ? systemInfo.users.total.toLocaleString() : <CircularProgress size={20} />}
                    </Typography>
                    {systemInfo && (
                      <Typography variant="caption" color="text.secondary">
                        {systemInfo.users.active} active
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CloudIcon sx={{ mr: 1, color: 'warning.main', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Server Uptime
                      </Typography>
                    </Box>
                    <Typography variant="h5" fontWeight={600} color="warning.main">
                      {systemInfo ? systemInfo.uptime : <CircularProgress size={20} />}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminSettings;
